/**
 * DecisionFlow — CompException
 * ui/src/components/admin/CompException.jsx
 * Stage 11 — Compliance Lifecycle UI
 *
 * Exceptions panel for comp_admin. Route: /admin/exceptions
 * Backend: exceptions_router.py (Stage 8 — live)
 * API contract: §18.5–18.8 (v1.11 — authoritative)
 *
 * D1: Slide-out drawer for detail + actions
 * D2: Modal form for create
 * D3: Appended to Execution group in CompAdminShell nav
 *
 * Lifecycle: pending → approved | rejected → applied (engine-set)
 * S584: withdrawal workflow added:
 *   approved → withdrawal_pending (CA1 requests with reason)
 *   withdrawal_pending → withdrawn (CA2 approves — four-eyes)
 *   withdrawal_pending → approved  (CA2 rejects — four-eyes)
 *   withdrawn → terminal
 *
 * NN-23: approved required before applied — server enforced, UI gates actions
 * Four-eyes: approved_by ≠ submitted_by — server enforced, UI surfaces error
 * Four-eyes: withdrawal_approved_by ≠ withdrawal_requested_by — server enforced
 * CC-1: comp_admin only — finance gets 403 on detail/patch (not a UI concern here)
 * DD-06: all data from live API
 * Accessibility §0.4.2: all pills use color + icon
 */

import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ShieldAlert, X, CheckCircle, XCircle, Clock, CheckCheck,
  AlertTriangle, Plus, ChevronDown, Lock,
} from "lucide-react";
import Card from "../shared/Card.jsx";
import Field from "../shared/Field.jsx";
import Pill from "../shared/Pill.jsx";
import Button from "../shared/Button.jsx";

const API_BASE = "/api/v1";


function useFetch(url) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!url) return;
    setLoading(true);
    fetch(url, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [url]);

  return { data, loading, error };
}

function LoadingRow() {
  return (
    <div className="space-y-2">
      <div className="skeleton h-4 w-2/3 rounded " />
      <div className="skeleton h-4 w-1/2 rounded " />
    </div>
  );
}

function ErrorNote({ message }) {
  return (
    <div className="rounded-xl bg-[rgb(var(--color-danger))]/10 px-3 py-2 text-sm text-[rgb(var(--color-danger))] ">
      {message ?? "Unable to load data. Please refresh."}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pill helpers — color + icon per §0.4.2 (no info by color alone)
// S584: withdrawal_pending and withdrawn added
// ---------------------------------------------------------------------------

const EXCEPTION_STATUS_CONFIG = {
  pending:            { tone: "Draft",    Icon: Clock,        label: "Pending"            },
  approved:           { tone: "Complete", Icon: CheckCircle,  label: "Approved"           },
  rejected:           { tone: "Blocked",  Icon: XCircle,      label: "Rejected"           },
  applied:            { tone: "Ready",    Icon: CheckCheck,   label: "Applied"            },
  withdrawal_pending: { tone: "Draft",    Icon: Clock,        label: "Withdrawal Pending" },
  withdrawn:          { tone: "Blocked",  Icon: XCircle,      label: "Withdrawn"          },
};

const EXCEPTION_TYPE_LABELS = {
  // Core exception types (Phase 1/2 — original values)
  ramp:                    "Ramp",
  leave:                   "Leave",
  territory_change:        "Territory Change",
  clawback:                "Clawback",
  retro_adjustment:        "Retro Adj.",
  // Modifier domain exception types (spec_exception_dispute_engine_v2_0.md §3.2, m128)
  kicker_exception:        "Kicker Exception",
  draw_exception:          "Draw Exception",
  split_exception:         "Split Exception",
  spiff_exception:         "Spiff Exception",
  transaction_exception:   "Transaction Exception",
  churn_clawback:          "Churn Clawback",
  revenue_type_exception:  "Revenue Type Exception",
};

// Modifier types requiring a linkage field in the create modal (spec §8.1 P3)
const MODIFIER_EXCEPTION_LINKAGE = {
  kicker_exception:       { field: "kicker_plan_id",              label: "Kicker Plan ID" },
  draw_exception:         { field: "draw_plan_id",                label: "Draw Plan ID" },
  split_exception:        { field: "split_plan_id",               label: "Split Plan ID" },
  spiff_exception:        { field: "spiff_plan_id",               label: "Spiff Plan ID" },
  transaction_exception:  { field: "transaction_plan_id",         label: "Transaction Plan ID" },
  churn_clawback:         { field: "clawback_policy_id",          label: "Clawback Policy ID" },
  revenue_type_exception: { field: "revenue_type_rate_table_id",  label: "Revenue Type Rate Table ID" },
};

function StatusPill({ status }) {
  const cfg = EXCEPTION_STATUS_CONFIG[status] ?? { tone: "Draft", Icon: Clock, label: status };
  return (
    <Pill tone={cfg.tone}>
      <span className="flex items-center gap-1">
        <cfg.Icon className="h-3 w-3" />
        {cfg.label}
      </span>
    </Pill>
  );
}

function TypePill({ type }) {
  return (
    <Pill tone="Draft">
      {EXCEPTION_TYPE_LABELS[type] ?? type}
    </Pill>
  );
}

// ---------------------------------------------------------------------------
// Format helpers
// ---------------------------------------------------------------------------

function fmt(val) {
  if (val == null || val === "") return "—";
  return val;
}

function fmtDate(val) {
  if (!val) return "—";
  return new Date(val).toLocaleDateString();
}

function fmtAmount(amount, currency) {
  if (amount == null) return "—";
  return `${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${currency ? " " + currency.trim() : ""}`;
}

// ---------------------------------------------------------------------------
// Create Modal (D2)
// ---------------------------------------------------------------------------

function CreateExceptionModal({ open, onClose, onCreated, period }) {
  const [form, setForm] = useState({
    exception_type:       "",
    reason:               "",
    position_id:          "",
    fiscal_period_id:     period?.fiscal_period_id ?? "",
    financial_impact:     "",
    impact_currency_code: "",
    is_blocking:          true,
    modifier_plan_id:     "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState(null);

  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const periods = periodsData?.fiscal_periods ?? periodsData?.data ?? [];
  const periodLabelById = Object.fromEntries(periods.map((p) => [String(p.fiscal_period_id), p.period_label ?? p.period_code]));

  useEffect(() => {
    if (period?.fiscal_period_id) {
      setForm((f) => ({ ...f, fiscal_period_id: period.fiscal_period_id }));
    }
  }, [period]);

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit() {
    if (!form.exception_type || !form.reason.trim() || !form.position_id.trim() || !form.fiscal_period_id) {
      setError("Exception type, reason, position ID, and fiscal period are required.");
      return;
    }
    const linkageCfg = MODIFIER_EXCEPTION_LINKAGE[form.exception_type];
    if (linkageCfg && !form.modifier_plan_id.trim()) {
      setError(`${linkageCfg.label} is required for ${EXCEPTION_TYPE_LABELS[form.exception_type]} exceptions.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        exception_type:   form.exception_type,
        reason:           form.reason.trim(),
        position_id:      form.position_id.trim(),
        fiscal_period_id: Number(form.fiscal_period_id),
        is_blocking:      form.is_blocking,
      };
      if (form.financial_impact !== "") {
        payload.financial_impact = Number(form.financial_impact);
      }
      if (form.impact_currency_code.trim() !== "") {
        payload.impact_currency_code = form.impact_currency_code.trim().toUpperCase();
      }
      if (linkageCfg && form.modifier_plan_id.trim()) {
        payload[linkageCfg.field] = Number(form.modifier_plan_id.trim());
      }
      const res = await fetch(`${API_BASE}/exceptions`, {
        method:  "POST",
        credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onCreated();
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[rgb(var(--color-text))]/30 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="w-full max-w-lg rounded-2xl bg-[rgb(var(--color-surface))] p-6 shadow-xl "
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-[rgb(var(--color-text))] ">
            New Exception
          </h2>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] "
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3">
          {/* Exception Type */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Exception Type <span className="text-red-500">*</span>
            </label>
            <select
              value={form.exception_type}
              onChange={(e) => set("exception_type", e.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            >
              <option value="">Select type…</option>
              {Object.entries(EXCEPTION_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Reason */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.reason}
              onChange={(e) => set("reason", e.target.value)}
              rows={3}
              placeholder="Describe the reason for this exception…"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none resize-none"
            />
          </div>

          {/* Position ID */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Position ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.position_id}
              onChange={(e) => set("position_id", e.target.value)}
              placeholder="UUID of affected position"
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            />
          </div>

          {/* Fiscal Period */}
          <div>
            <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Fiscal Period <span className="text-red-500">*</span>
            </label>
            <select
              value={form.fiscal_period_id}
              onChange={(e) => set("fiscal_period_id", e.target.value)}
              className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
            >
              <option value="">Select period…</option>
              {periods.map((p) => (
                <option key={p.fiscal_period_id} value={p.fiscal_period_id}>
                  {p.period_label ?? p.period_code}
                </option>
              ))}
            </select>
          </div>

          {/* Modifier Linkage Field */}
          {MODIFIER_EXCEPTION_LINKAGE[form.exception_type] && (
            <div>
              <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                {MODIFIER_EXCEPTION_LINKAGE[form.exception_type].label} <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.modifier_plan_id}
                onChange={(e) => set("modifier_plan_id", e.target.value)}
                placeholder="Integer ID of the linked plan"
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
              />
            </div>
          )}

          {/* is_blocking toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="exc-is-blocking"
              checked={form.is_blocking}
              onChange={(e) => set("is_blocking", e.target.checked)}
              className="h-4 w-4 rounded border-[rgb(var(--color-border))] accent-[rgb(var(--color-primary))]"
            />
            <label htmlFor="exc-is-blocking" className="text-xs font-medium text-[rgb(var(--color-text-muted))]">
              Blocking exception (holds engine calculation for this position until resolved or rejected)
            </label>
          </div>

          {form.is_blocking && form.exception_type && (
            <div className="rounded-xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 px-4 py-3 text-xs text-[rgb(var(--color-warning))]">
              This blocking exception will hold the {EXCEPTION_TYPE_LABELS[form.exception_type] ?? form.exception_type} calculation
              for this position until the exception is resolved or rejected.
            </div>
          )}

          {/* Financial Impact */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                Financial Impact
              </label>
              <input
                type="number"
                value={form.financial_impact}
                onChange={(e) => set("financial_impact", e.target.value)}
                placeholder="0.00"
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                Currency
              </label>
              <input
                type="text"
                value={form.impact_currency_code}
                onChange={(e) => set("impact_currency_code", e.target.value)}
                placeholder="USD"
                maxLength={3}
                className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none"
              />
            </div>
          </div>

          {error && <ErrorNote message={error} />}

          <div className="flex gap-2 pt-1">
            <Button onClick={handleSubmit} disabled={submitting}>
              {submitting ? "Submitting…" : "Submit Exception"}
            </Button>
            <Button variant="secondary" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExceptionDrawer (D1) — slide-out detail + actions
// S584: withdrawal workflow added — three new action branches
// ---------------------------------------------------------------------------

function ExceptionDrawer({ exception, open, onClose, onUpdated, periodLabelById }) {
  const [submitting, setSubmitting]           = useState(false);
  const [error, setError]                     = useState(null);
  const [rejectMode, setRejectMode]           = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  // S584: withdrawal state
  const [withdrawRequestMode, setWithdrawRequestMode] = useState(false);
  const [withdrawalReason,    setWithdrawalReason]    = useState("");

  if (!exception) return null;

  // ── Standard actions ────────────────────────────────────────────────────

  async function handleApprove() {
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/exceptions/${exception.exception_id}`, {
        method: "PATCH", credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exception_status: "approved" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onUpdated();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  async function handleReject() {
    if (!rejectionReason.trim()) return;
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/exceptions/${exception.exception_id}`, {
        method: "PATCH", credentials: 'include',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          exception_status: "rejected",
          rejection_reason: rejectionReason.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onUpdated();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  // ── S584: Withdrawal actions ────────────────────────────────────────────

  async function handleRequestWithdrawal() {
    if (!withdrawalReason.trim()) return;
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/exceptions/${exception.exception_id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          exception_status: "withdrawal_pending",
          withdrawal_reason: withdrawalReason.trim(),
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onUpdated();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  async function handleApproveWithdrawal() {
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/exceptions/${exception.exception_id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exception_status: "withdrawn" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onUpdated();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  async function handleRejectWithdrawal() {
    setSubmitting(true); setError(null);
    try {
      const res = await fetch(`${API_BASE}/exceptions/${exception.exception_id}`, {
        method: "PATCH", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ exception_status: "approved" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body?.error?.message ?? `Error ${res.status}`);
        return;
      }
      onUpdated();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-[rgb(var(--color-text))]/20"
            onClick={onClose}
          />

          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] shadow-xl  "
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-[rgb(var(--color-border))] px-5 py-4 ">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />
                <span className="text-sm font-semibold text-[rgb(var(--color-text))] ">
                  Exception Detail
                </span>
              </div>
              <div className="flex items-center gap-2">
                <TypePill type={exception.exception_type} />
                <StatusPill status={exception.exception_status} />
                <button
                  onClick={onClose}
                  className="ml-2 rounded-lg p-1 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] "
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

              {/* Core fields */}
              <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-3 ">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Exception Type" value={EXCEPTION_TYPE_LABELS[exception.exception_type] ?? exception.exception_type} />
                  <Field label="Status"         value={exception.exception_status} />
                </div>
                <Field label="Reason" value={fmt(exception.reason)} />
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Position ID"   value={fmt(exception.position_id)} />
                  <Field label="Fiscal Period" value={periodLabelById[String(exception.fiscal_period_id)] ?? fmt(exception.fiscal_period_id)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Submitted By" value={fmt(exception.submitted_by)} />
                  <Field label="Submitted At" value={fmtDate(exception.submitted_at)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Financial Impact" value={fmtAmount(exception.financial_impact, exception.impact_currency_code)} />
                  <Field label="Blocking"         value={exception.is_blocking ? "Yes" : "No"} />
                </div>
                {MODIFIER_EXCEPTION_LINKAGE[exception.exception_type] && (() => {
                  const cfg   = MODIFIER_EXCEPTION_LINKAGE[exception.exception_type];
                  const value = exception[cfg.field];
                  return value != null ? (
                    <Field label={cfg.label} value={fmt(String(value))} />
                  ) : null;
                })()}
              </div>

              {/* Approval block */}
              {(exception.approved_by || exception.approved_at) && (
                <div className="rounded-xl border border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/10 px-4 py-3 space-y-2  ">
                  <p className="text-xs font-semibold text-[rgb(var(--color-success))] ">Approval</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Approved By" value={fmt(exception.approved_by)} />
                    <Field label="Approved At" value={fmtDate(exception.approved_at)} />
                  </div>
                </div>
              )}

              {/* Rejection block */}
              {exception.exception_status === "rejected" && (
                <div className="rounded-xl border border-[rgb(var(--color-danger))]/30 bg-[rgb(var(--color-danger))]/10 px-4 py-3 space-y-2  ">
                  <p className="text-xs font-semibold text-[rgb(var(--color-danger))] ">Rejection</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Rejected By" value={fmt(exception.rejected_by)} />
                    <Field label="Rejected At" value={fmtDate(exception.rejected_at)} />
                  </div>
                  <Field label="Rejection Reason" value={fmt(exception.rejection_reason)} />
                </div>
              )}

              {/* Applied block */}
              {exception.exception_status === "applied" && (
                <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-2 ">
                  <p className="text-xs font-semibold text-[rgb(var(--color-text-muted))]">Applied</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Applied In Run" value={fmt(exception.applied_in_run_id)} />
                    <Field label="Applied At"     value={fmtDate(exception.applied_at)} />
                  </div>
                  {exception.original_payout_summary_id && (
                    <Field label="Original Payout Summary" value={exception.original_payout_summary_id} />
                  )}
                </div>
              )}

              {/* S584: Withdrawal detail block — shown when withdrawn */}
              {exception.exception_status === "withdrawn" && (
                <div className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-3 space-y-2">
                  <p className="text-xs font-semibold text-[rgb(var(--color-text-muted))]">Withdrawn</p>
                  <Field label="Withdrawal Reason"      value={fmt(exception.withdrawal_reason)} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Requested By" value={fmt(exception.withdrawal_requested_by)} />
                    <Field label="Requested At" value={fmtDate(exception.withdrawal_requested_at)} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Approved By" value={fmt(exception.withdrawal_approved_by)} />
                    <Field label="Withdrawn At" value={fmtDate(exception.withdrawal_approved_at)} />
                  </div>
                </div>
              )}

              {error && <ErrorNote message={error} />}

              {/* ── Actions — gated by exception_status ── */}

              {/* pending → approve | reject */}
              {exception.exception_status === "pending" && (
                <div className="space-y-3">
                  {!rejectMode ? (
                    <div className="flex gap-2">
                      <Button onClick={handleApprove} disabled={submitting}>
                        <CheckCircle className="h-4 w-4" />
                        {submitting ? "Approving…" : "Approve"}
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => { setRejectMode(true); setError(null); }}
                        disabled={submitting}
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2 rounded-xl border border-[rgb(var(--color-danger))]/30 p-3 ">
                      <label className="block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                        Rejection Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        placeholder="Required — state the reason for rejection…"
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm   focus:outline-none resize-none"
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="danger"
                          onClick={handleReject}
                          disabled={submitting || !rejectionReason.trim()}
                        >
                          {submitting ? "Rejecting…" : "Confirm Reject"}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => { setRejectMode(false); setError(null); }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── S584: approved → withdrawal_pending ──────────────────────── */}
              {exception.exception_status === "approved" && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[rgb(var(--color-success))]/20 bg-[rgb(var(--color-success))]/5 px-4 py-3 text-xs text-[rgb(var(--color-text-muted))]">
                    <strong className="text-[rgb(var(--color-success))]">
                      Approved — queued for engine application.
                    </strong>{" "}
                    This exception will be applied on the next calculation run for this period.
  If this exception was approved in error, request withdrawal below.
                  </div>

                  {!withdrawRequestMode ? (
                    <Button
                      variant="secondary"
                      onClick={() => { setWithdrawRequestMode(true); setError(null); }}
                      disabled={submitting}
                    >
                      Request Withdrawal
                    </Button>
                  ) : (
                    <div className="space-y-2 rounded-xl border border-[rgb(var(--color-warning))]/30 p-3">
                      <p className="text-xs font-semibold text-[rgb(var(--color-warning))]">
                        Request Withdrawal
                      </p>
                      <p className="text-xs text-[rgb(var(--color-text-muted))]">
                        A second comp admin must approve this withdrawal request before the exception is reversed.
                      </p>
                      <label className="block text-xs font-medium text-[rgb(var(--color-text-muted))]">
                        Withdrawal Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={withdrawalReason}
                        onChange={(e) => setWithdrawalReason(e.target.value)}
                        rows={3}
                        placeholder="Required — state the reason for withdrawal…"
                        className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-sm focus:outline-none resize-none"
                      />
                      <div className="flex gap-2">
                        <Button
                          variant="danger"
                          onClick={handleRequestWithdrawal}
                          disabled={submitting || !withdrawalReason.trim()}
                        >
                          {submitting ? "Requesting…" : "Confirm Withdrawal Request"}
                        </Button>
                        <Button
                          variant="secondary"
                          onClick={() => { setWithdrawRequestMode(false); setWithdrawalReason(""); setError(null); }}
                          disabled={submitting}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── S584: withdrawal_pending → withdrawn | approved ───────────── */}
              {exception.exception_status === "withdrawal_pending" && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[rgb(var(--color-warning))]/20 bg-[rgb(var(--color-warning))]/5 px-4 py-3 space-y-2">
                    <p className="text-xs font-semibold text-[rgb(var(--color-warning))]">
                      Withdrawal Requested
                    </p>
                    <Field label="Reason"       value={fmt(exception.withdrawal_reason)} />
                    <div className="grid grid-cols-2 gap-3">
                      <Field label="Requested By" value={fmt(exception.withdrawal_requested_by)} />
                      <Field label="Requested At" value={fmtDate(exception.withdrawal_requested_at)} />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={handleApproveWithdrawal} disabled={submitting}>
                      <CheckCircle className="h-4 w-4" />
                      {submitting ? "Processing…" : "Approve Withdrawal"}
                    </Button>
                    <Button variant="secondary" onClick={handleRejectWithdrawal} disabled={submitting}>
                      <XCircle className="h-4 w-4" />
                      Reject Withdrawal
                    </Button>
                  </div>

                  <p className="text-xs text-[rgb(var(--color-text-muted))]">
                    Four-eyes required — a different comp admin must approve or reject this request.
                    The server enforces this and will reject if you are the same user who requested withdrawal.
                  </p>
                </div>
              )}

              {/* withdrawn — terminal, no actions */}
              {exception.exception_status === "withdrawn" && (
                <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-3 text-xs text-[rgb(var(--color-text-muted))]">
                  This exception has been withdrawn and will not be applied by the engine.
                  No further action is available.
                </div>
              )}

            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ---------------------------------------------------------------------------
// CompException — main panel
// ---------------------------------------------------------------------------

export default function CompException({ period }) {
  // S603 OI-LOCKED-PERIOD-WRITE-SUPPRESSION — matches CalculationPanel pattern exactly.
  const isLocked = period?.period_status !== "open";

  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const [selectedId, setSelectedId]     = useState(null);
  const [drawerOpen, setDrawerOpen]     = useState(false);
  const [modalOpen, setModalOpen]       = useState(false);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterType,   setFilterType]   = useState("");

  const { data: periodsData } = useFetch(`${API_BASE}/fiscal-periods`);
  const periods = periodsData?.fiscal_periods ?? periodsData?.data ?? [];
  const periodLabelById = Object.fromEntries(periods.map((p) => [String(p.fiscal_period_id), p.period_label ?? p.period_code]));

  function fetchExceptions() {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/exceptions`, { credentials: 'include' })
      .then((r) => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then((d) => setExceptions(d?.data ?? d?.exceptions ?? []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchExceptions(); }, []);

  const selectedRecord = exceptions.find((e) => e.exception_id === selectedId) ?? null;

  function openDrawer(exc) {
    setSelectedId(exc.exception_id);
    setDrawerOpen(true);
  }

  function closeDrawer() {
    setDrawerOpen(false);
    setSelectedId(null);
  }

  function handleCreated() {
    setModalOpen(false);
    fetchExceptions();
  }

  function handleUpdated() {
    closeDrawer();
    fetchExceptions();
  }

  const filtered = exceptions.filter((e) => {
    if (filterStatus && e.exception_status !== filterStatus) return false;
    if (filterType   && e.exception_type   !== filterType)   return false;
    return true;
  });

  return (
    <>
      <div className="w-full">
        <Card
          title="Exceptions"
          action={
            isLocked ? (
              <div className="flex items-center gap-1.5 rounded-lg border border-[rgb(var(--color-warning))]/30 bg-[rgb(var(--color-warning))]/8 px-2.5 py-1.5">
                <Lock className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-warning))]" />
                <span className="text-tok-xs text-[rgb(var(--color-warning))]">Period {period?.period_status}</span>
              </div>
            ) : (
              <Button onClick={() => setModalOpen(true)}>
                <Plus className="h-4 w-4" />
                New Exception
              </Button>
            )
          }
        >
          {/* Filters */}
          <div className="mb-3 flex flex-wrap gap-2">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-xs   focus:outline-none"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="applied">Applied</option>
              <option value="withdrawal_pending">Withdrawal Pending</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-1.5 text-xs   focus:outline-none"
            >
              <option value="">All Types</option>
              {Object.entries(EXCEPTION_TYPE_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          {loading && <LoadingRow />}
          {error   && <ErrorNote message="Unable to load exceptions." />}

          {!loading && !error && filtered.length === 0 && (
            <p className="text-sm text-[rgb(var(--color-text-muted))]">
              {exceptions.length === 0
                ? "No exceptions found for this tenant."
                : "No exceptions match the selected filters."}
            </p>
          )}

          <div className="space-y-2">
            {filtered.map((exc) => (
              <button
                key={exc.exception_id}
                onClick={() => openDrawer(exc)}
                className="w-full text-left rounded-xl border border-[rgb(var(--color-border))] px-3 py-2.5 transition hover:border-[rgb(var(--color-border-strong))] hover:bg-[rgb(var(--color-surface-muted))]  "
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="grid flex-1 gap-1 sm:grid-cols-3">
                    <Field label="Type"      value={EXCEPTION_TYPE_LABELS[exc.exception_type] ?? exc.exception_type} />
                    <Field label="Period"    value={periodLabelById[String(exc.fiscal_period_id)] ?? fmt(exc.fiscal_period_id)} />
                    <Field label="Submitted" value={fmtDate(exc.submitted_at)} />
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <StatusPill status={exc.exception_status} />
                    {exc.is_blocking && (
                      <span className="flex items-center gap-0.5 text-xs text-amber-600 ">
                        <AlertTriangle className="h-3 w-3" /> Blocking
                      </span>
                    )}
                  </div>
                </div>
                {exc.reason && (
                  <p className="mt-1.5 truncate text-xs text-[rgb(var(--color-text-muted))]">
                    {exc.reason.length > 60 ? exc.reason.slice(0, 60) + "…" : exc.reason}
                  </p>
                )}
              </button>
            ))}
          </div>
        </Card>
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {modalOpen && (
          <CreateExceptionModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            onCreated={handleCreated}
            period={period}
          />
        )}
      </AnimatePresence>

      {/* Detail Drawer */}
      <ExceptionDrawer
        exception={selectedRecord}
        open={drawerOpen}
        onClose={closeDrawer}
        onUpdated={handleUpdated}
        periodLabelById={periodLabelById}
      />
    </>
  );
}
